import { useQuery } from '@tanstack/react-query';
import { MailPlus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { CreatedInvitation, Invitation } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/Button';
import { Alert, EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/**
 * IDN-02: people join through Lixionary IAM. Inviting gives the address its Jira role in IAM; IAM emails people
 * who have no Lixionary account yet. The list shows invitees who have not signed in.
 */
export function InvitationsPage() {
  usePageTitle('Invitations');
  useCrumbs([{ label: 'Administration' }, { label: 'Invitations' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.get<Invitation[]>('/admin/invitations'),
    enabled: admin,
  });
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<Invitation | null>(null);
  const cancel = useAction((i: Invitation) => api.post(`/admin/invitations/${i.id}/revoke`), {
    success: 'Invitation cancelled',
    invalidate: [['invitations'], ['users']],
    onSuccess: () => setCancelling(null),
  });
  const resend = useAction((i: Invitation) => api.post(`/admin/invitations/${i.id}/resend`), {
    success: 'Lixionary IAM sent the invitation again',
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Invitations"
        subtitle="Lixionary IAM emails the invitation to people without a Lixionary account. People who already have one get access at once."
        actions={<Button onClick={() => setCreating(true)}>Create invitation</Button>}
      />
      <QueryView query={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState icon={MailPlus} title="No open invitations">
              Everyone you invited has signed in. Create an invitation to admit someone.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Invited</th>
                    <th>Invited by</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((i) => (
                    <tr key={i.id}>
                      <td className="ink">{i.email}</td>
                      <td className="small">{i.invitedAt ? <Timestamp value={i.invitedAt} /> : '—'}</td>
                      <td className="small">
                        <ActorName actor={i.invitedBy} />
                      </td>
                      <td>
                        <div className="row nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={resend.isPending && resend.variables === i}
                            onClick={() => resend.mutate(i)}
                          >
                            Resend
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setCancelling(i)}>
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryView>
      <ErrorAlert error={resend.error} />
      {creating && <CreateInvitation onClose={() => setCreating(false)} />}
      {cancelling && (
        <ConfirmDialog
          title={`Cancel the invitation for ${cancelling.email}?`}
          confirmLabel="Yes, cancel"
          variant="danger"
          onClose={() => setCancelling(null)}
          onConfirm={() => cancel.mutate(cancelling)}
          pending={cancel.isPending}
          error={cancel.error}
        >
          <p>
            The invitation link stops working and the person loses the Jira role. Lixionary IAM removes the
            account it created for them unless another application uses it.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function CreateInvitation({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const create = useAction(() => api.post<CreatedInvitation>('/admin/invitations', { email }), {
    invalidate: [['invitations'], ['users']],
  });
  if (create.data) {
    const result = create.data;
    return (
      <Modal title="Invitation sent" onClose={onClose} actions={<Button onClick={onClose}>Close</Button>}>
        {result.created ? (
          <Alert tone="success" title={`Lixionary IAM invited ${email}`}>
            They receive an email to set up their Lixionary account, then continue to Jira.
          </Alert>
        ) : (
          <Alert tone="success" title={`${email} can sign in now`}>
            They already have a Lixionary account, so their access took effect immediately.
            {result.notification.status === 'sent' && ' We emailed them that they have access.'}
            {result.notification.status === 'failed' &&
              ` The notification email failed (${result.notification.error}); let them know yourself.`}
          </Alert>
        )}
        <p className="small muted">
          Add them to projects, or make them an admin, from{' '}
          <Link to={`/admin/users/${result.id}`} onClick={onClose}>
            their user page
          </Link>
          .
        </p>
      </Modal>
    );
  }
  return (
    <Modal
      title="Create invitation"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate(undefined)} loading={create.isPending} disabled={!email}>
            Create invitation
          </Button>
        </>
      }
    >
      <Field
        label="Email"
        hint="They join as a user with no projects. Change their role and add projects from their user page."
      >
        {(p) => (
          <Input
            {...p}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-autofocus
          />
        )}
      </Field>
      <ErrorAlert error={create.error} />
    </Modal>
  );
}
